import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Edit, Refresh } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import CustomPagination from '../../components/common/CustomPagination';

import {
  createMarketplaceWarehouse,
  createMarketplaceRateCard,
  createMarketplaceStorageAccount,
  getMarketplaceInboundShipments,
  getMarketplaceInboundShipment,
  getMarketplaceCharges,
  getMarketplaceInventoryLots,
  getMarketplaceInvoices,
  getMarketplaceRateCards,
  getMarketplaceStorageAccounts,
  getMarketplaceWarehouseLocations,
  getMarketplaceWarehouses,
  generateMarketplaceStorageCharges,
  issueMarketplaceInvoice,
  receiveMarketplaceInboundShipment,
  scanMarketplaceWarehouseLabel,
  updateMarketplaceInboundShipment,
  updateMarketplaceRateCard,
  updateMarketplaceWarehouse,
} from '../../services/marketplaceWarehouse';
import { getMerchants } from '../../services/merchantService';

const PRICING_MODE_OPTIONS = [
  { value: 'flat', label: 'Flat Fee' },
  { value: 'percentage', label: 'Percentage Only' },
  { value: 'hybrid', label: 'Flat + Percentage' },
];

const getPricingModeLabel = (value) => (
  PRICING_MODE_OPTIONS.find((option) => option.value === value)?.label || value || 'Flat Fee'
);

const PAGE_CONFIG = {
  '/admin/merchant-admin/warehouses': {
    title: 'Cloud Warehouse Facilities',
    description: 'Manage the physical facilities that merchants rent for Checkstall cloud warehousing.',
    loader: getMarketplaceWarehouses,
    columns: [
      { field: 'code', headerName: 'Code', width: 140 },
      { field: 'name', headerName: 'Warehouse', flex: 1, minWidth: 180 },
      { field: 'city', headerName: 'City', width: 140 },
      { field: 'state', headerName: 'State', width: 140 },
      { field: 'floor_area_sqm', headerName: 'Floor Area (sqm)', width: 150 },
      { field: 'ceiling_height_m', headerName: 'Ceiling Height (m)', width: 160 },
      { field: 'max_capacity_cbm', headerName: 'Capacity (CBM)', width: 150 },
      { field: 'address', headerName: 'Address', flex: 1.5, minWidth: 240 },
      {
        field: 'is_active',
        headerName: 'Status',
        width: 120,
        renderCell: ({ value }) => <Chip label={value ? 'Active' : 'Inactive'} color={value ? 'success' : 'default'} size="small" />,
      },
    ],
  },
  '/admin/merchant-admin/rate-cards': {
    title: 'Cloud Warehouse Rate Cards',
    description: 'Configure internal warehouse pricing. Merchants see combined fulfillment fees; packaging materials are billed from actual worker selections.',
    loader: getMarketplaceRateCards,
    columns: [
      { field: 'name', headerName: 'Rate Card', flex: 1, minWidth: 200 },
      { field: 'currency', headerName: 'Currency', width: 110 },
      {
        field: 'receiving_pricing_mode',
        headerName: 'Receiving Mode',
        width: 160,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'receiving_fee_per_cbm', headerName: 'Receive Flat / CBM', width: 155 },
      { field: 'receiving_percentage_rate', headerName: 'Receive %', width: 125 },
      { field: 'receiving_minimum_fee', headerName: 'Receive Min', width: 135 },
      {
        field: 'storage_pricing_mode',
        headerName: 'Storage Mode',
        width: 150,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'storage_fee_per_cbm_per_month', headerName: 'Storage Flat / CBM / Month', width: 210 },
      { field: 'storage_percentage_rate', headerName: 'Storage %', width: 125 },
      {
        field: 'overstay_pricing_mode',
        headerName: 'Overstay Mode',
        width: 155,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'overstay_fee_per_cbm_per_month', headerName: 'Overstay Flat / CBM / Month', width: 215 },
      { field: 'overstay_percentage_rate', headerName: 'Overstay %', width: 130 },
      { field: 'overstay_minimum_fee', headerName: 'Overstay Min', width: 140 },
      {
        field: 'fulfillment_pricing_mode',
        headerName: 'Fulfillment Mode',
        width: 170,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'fulfillment_fee_per_order', headerName: 'Fulfillment Flat', width: 150 },
      { field: 'fulfillment_percentage_rate', headerName: 'Fulfillment %', width: 140 },
      { field: 'fulfillment_minimum_fee', headerName: 'Fulfillment Min', width: 150 },
      {
        field: 'pick_pricing_mode',
        headerName: 'Additional Item Picking Mode',
        width: 150,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'pick_fee_per_item', headerName: 'Additional Item Pick Flat', width: 185 },
      { field: 'pick_percentage_rate', headerName: 'Additional Item Pick %', width: 180 },
      { field: 'pick_minimum_fee', headerName: 'Additional Item Pick Min', width: 185 },
      {
        field: 'shipping_handling_pricing_mode',
        headerName: 'Shipping Mode',
        width: 160,
        renderCell: ({ value }) => getPricingModeLabel(value),
      },
      { field: 'shipping_handling_fee_per_package', headerName: 'Shipping Flat', width: 145 },
      { field: 'shipping_handling_percentage_rate', headerName: 'Shipping %', width: 130 },
      { field: 'shipping_handling_minimum_fee', headerName: 'Shipping Min', width: 140 },
      { field: 'declared_value_alert_percent_of_listing', headerName: 'Low Value Alert %', width: 150 },
      { field: 'overstay_after_months', headerName: 'Overstay After (Months)', width: 180 },
    ],
  },
  '/admin/merchant-admin/storage-accounts': {
    title: 'Merchant Storage Accounts',
    description: 'Track which merchants have active cloud warehouse accounts and which rate card each account uses.',
    loader: getMarketplaceStorageAccounts,
    columns: [
      { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 220 },
      { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 180 },
      { field: 'rate_card_name', headerName: 'Rate Card', flex: 1, minWidth: 180 },
      { field: 'billing_cycle_day', headerName: 'Billing Day', width: 120 },
      { field: 'payment_terms_days', headerName: 'Terms (Days)', width: 130 },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: ({ value }) => <Chip label={value} size="small" color={value === 'active' ? 'success' : 'default'} />,
      },
    ],
  },
  '/admin/merchant-admin/inbound-shipments': {
    title: 'Cloud Warehouse Inbounds',
    description: 'Track inbound stock, declared CBM, receiving status, and billable warehouse intake for merchant inventory.',
    loader: getMarketplaceInboundShipments,
    columns: [
      { field: 'inbound_number', headerName: 'Inbound Number', width: 180 },
      { field: 'merchant_sid', headerName: 'Merchant SID', flex: 1, minWidth: 240 },
      { field: 'warehouse_sid', headerName: 'Warehouse SID', flex: 1, minWidth: 220 },
      { field: 'tracking_number', headerName: 'Tracking Number', width: 180 },
      { field: 'package_count', headerName: 'Packages', width: 110 },
      { field: 'declared_total_cbm', headerName: 'Declared CBM', width: 130 },
      { field: 'declared_total_value', headerName: 'Declared Value', width: 140 },
      { field: 'reviewed_total_value', headerName: 'Reviewed Value', width: 140 },
      { field: 'received_total_cbm', headerName: 'Received CBM', width: 130 },
      {
        field: 'is_value_flagged',
        headerName: 'Value Alert',
        width: 125,
        renderCell: ({ value }) => <Chip label={value ? 'Flagged' : 'Clear'} color={value ? 'warning' : 'success'} size="small" />,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 170,
        renderCell: ({ value }) => <Chip label={(value || '').replaceAll('_', ' ')} size="small" />,
      },
    ],
  },
  '/admin/merchant-admin/inventory': {
    title: 'Cloud Warehouse Inventory',
    description: 'Review storage lots, billable CBM, storage dates, and overstay exposure for merchant stock.',
    loader: getMarketplaceInventoryLots,
    columns: [
      { field: 'sid', headerName: 'Lot SID', flex: 1, minWidth: 250 },
      { field: 'lot_number', headerName: 'Lot Number', width: 180 },
      { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 180 },
      { field: 'product_variant', headerName: 'Variant ID', width: 120 },
      { field: 'quantity_on_hand', headerName: 'On Hand', width: 120 },
      { field: 'quantity_reserved', headerName: 'Reserved', width: 120 },
      { field: 'quantity_available', headerName: 'Available', width: 120 },
      { field: 'quantity_damaged', headerName: 'Damaged', width: 120 },
      { field: 'billable_cbm', headerName: 'Billable CBM', width: 130 },
      { field: 'declared_total_value', headerName: 'Declared Value', width: 140 },
      { field: 'reviewed_total_value', headerName: 'Reviewed Value', width: 140 },
      {
        field: 'is_value_flagged',
        headerName: 'Value Alert',
        width: 125,
        renderCell: ({ value }) => <Chip label={value ? 'Flagged' : 'Clear'} color={value ? 'warning' : 'success'} size="small" />,
      },
      { field: 'overstay_starts_at', headerName: 'Overstay Starts', width: 180 },
    ],
  },
  '/admin/merchant-admin/charges': {
    title: 'Warehouse Charges',
    description: 'Review merchant charges created by receiving, storage, and overstay billing rules.',
    loader: getMarketplaceCharges,
    columns: [
      { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 200 },
      { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 180 },
      { field: 'charge_type', headerName: 'Charge Type', width: 150 },
      { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 260 },
      { field: 'quantity', headerName: 'Quantity', width: 110 },
      { field: 'rate', headerName: 'Rate', width: 110 },
      { field: 'amount', headerName: 'Amount', width: 120 },
      { field: 'currency', headerName: 'Currency', width: 100 },
      { field: 'status', headerName: 'Status', width: 110 },
    ],
  },
  '/admin/merchant-admin/invoices': {
    title: 'Warehouse Invoices',
    description: 'Review invoices raised from the accumulated merchant warehouse charges.',
    loader: getMarketplaceInvoices,
    columns: [
      { field: 'invoice_number', headerName: 'Invoice Number', width: 180 },
      { field: 'merchant_name', headerName: 'Merchant', flex: 1, minWidth: 200 },
      { field: 'warehouse_name', headerName: 'Warehouse', flex: 1, minWidth: 180 },
      { field: 'subtotal', headerName: 'Subtotal', width: 120 },
      { field: 'total', headerName: 'Total', width: 120 },
      { field: 'currency', headerName: 'Currency', width: 100 },
      { field: 'status', headerName: 'Status', width: 110 },
    ],
  },
};

const initialWarehouseForm = {
  code: '',
  name: '',
  city: '',
  state: '',
  country: 'Nigeria',
  address: '',
  floor_area_sqm: '',
  ceiling_height_m: '',
  max_capacity_cbm: '',
  is_active: true,
};

const initialRateCardForm = {
  warehouse_sid: '',
  name: '',
  currency: 'NGN',
  receiving_fee_per_cbm: '',
  receiving_pricing_mode: 'flat',
  receiving_percentage_rate: '',
  receiving_minimum_fee: '',
  storage_fee_per_cbm_per_month: '',
  storage_pricing_mode: 'flat',
  storage_percentage_rate: '',
  overstay_fee_per_cbm_per_month: '',
  overstay_pricing_mode: 'flat',
  overstay_percentage_rate: '',
  overstay_minimum_fee: '',
  overstay_after_months: 6,
  fulfillment_fee_per_order: '',
  fulfillment_pricing_mode: 'flat',
  fulfillment_percentage_rate: '',
  fulfillment_minimum_fee: '',
  pick_fee_per_item: '',
  pick_pricing_mode: 'flat',
  pick_percentage_rate: '',
  pick_minimum_fee: '',
  packaging_fee_per_package: '',
  packaging_pricing_mode: 'flat',
  packaging_percentage_rate: '',
  packaging_minimum_fee: '',
  shipping_handling_fee_per_package: '',
  shipping_handling_pricing_mode: 'flat',
  shipping_handling_percentage_rate: '',
  shipping_handling_minimum_fee: '',
  minimum_storage_charge: '',
  declared_value_alert_percent_of_listing: 50,
  grace_period_days: 0,
  is_default: false,
  is_active: true,
  notes: '',
};

const initialInboundReviewForm = {
  sid: '',
  inbound_number: '',
  declared_total_value: 0,
  reviewed_total_value: '',
  is_value_flagged: false,
  value_review_notes: '',
  lines: [],
};

const initialStorageAccountForm = {
  merchant_sid: '',
  warehouse_sid: '',
  rate_card_sid: '',
  billing_cycle_day: 1,
  payment_terms_days: 7,
  auto_invoice_enabled: true,
  notes: '',
};

const MarketplaceWarehousePage = () => {
  const theme = useTheme();
  const location = useLocation();
  const config = PAGE_CONFIG[location.pathname] || PAGE_CONFIG['/admin/merchant-admin/warehouses'];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiving, setReceiving] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedInbound, setSelectedInbound] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [savingInboundReview, setSavingInboundReview] = useState(false);
  const [inboundReviewForm, setInboundReviewForm] = useState(initialInboundReviewForm);
  const [locations, setLocations] = useState([]);
  const [receiptLines, setReceiptLines] = useState([]);
  const [scanLabel, setScanLabel] = useState('');
  const [issuingInvoiceFor, setIssuingInvoiceFor] = useState('');
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [savingWarehouse, setSavingWarehouse] = useState(false);
  const [editingWarehouseSid, setEditingWarehouseSid] = useState('');
  const [warehouseForm, setWarehouseForm] = useState(initialWarehouseForm);
  const [rateCardDialogOpen, setRateCardDialogOpen] = useState(false);
  const [savingRateCard, setSavingRateCard] = useState(false);
  const [editingRateCardSid, setEditingRateCardSid] = useState('');
  const [rateCardForm, setRateCardForm] = useState(initialRateCardForm);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [storageAccountDialogOpen, setStorageAccountDialogOpen] = useState(false);
  const [savingStorageAccount, setSavingStorageAccount] = useState(false);
  const [storageAccountForm, setStorageAccountForm] = useState(initialStorageAccountForm);
  const [merchantOptions, setMerchantOptions] = useState([]);
  const [storageRateCardOptions, setStorageRateCardOptions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await config.loader();
      setRows(result.rows);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to load marketplace warehouse data.');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const gridRows = useMemo(
    () => rows.map((row) => ({ ...row, id: row.sid || row.id })),
    [rows],
  );

  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return gridRows.slice(startIndex, startIndex + rowsPerPage);
  }, [gridRows, page, rowsPerPage]);

  const totalPages = useMemo(() => {
    if (!gridRows.length) return 0;
    return Math.ceil(gridRows.length / rowsPerPage);
  }, [gridRows.length, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [location.pathname]);

  useEffect(() => {
    const loadWarehouseOptions = async () => {
      if (![
        '/admin/merchant-admin/rate-cards',
        '/admin/merchant-admin/storage-accounts',
      ].includes(location.pathname)) return;
      try {
        const result = await getMarketplaceWarehouses();
        setWarehouseOptions(result.rows);
      } catch (requestError) {
        setError(requestError.response?.data?.detail || requestError.message || 'Unable to load warehouse options.');
      }
    };
    loadWarehouseOptions();
  }, [location.pathname]);

  const handleOpenCreateStorageAccount = async () => {
    setError('');
    try {
      const [merchantResult, rateCardResult, warehouseResult] = await Promise.all([
        getMerchants({ page_size: 1000 }),
        getMarketplaceRateCards(),
        getMarketplaceWarehouses(),
      ]);
      setMerchantOptions(merchantResult.data?.merchants || []);
      setStorageRateCardOptions(rateCardResult.rows);
      setWarehouseOptions(warehouseResult.rows);
      setStorageAccountForm(initialStorageAccountForm);
      setStorageAccountDialogOpen(true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to load storage account options.');
    }
  };

  const handleStorageAccountFormChange = (field) => (event) => {
    setStorageAccountForm((current) => ({
      ...current,
      [field]: event.target.value,
      ...(field === 'warehouse_sid' ? { rate_card_sid: '' } : {}),
    }));
  };

  const handleStorageAccountBooleanChange = (event) => {
    setStorageAccountForm((current) => ({
      ...current,
      auto_invoice_enabled: event.target.value === 'true',
    }));
  };

  const handleSaveStorageAccount = async () => {
    setSavingStorageAccount(true);
    setError('');
    try {
      await createMarketplaceStorageAccount({
        merchant_sid: storageAccountForm.merchant_sid,
        warehouse_sid: storageAccountForm.warehouse_sid,
        rate_card_sid: storageAccountForm.rate_card_sid || null,
        billing_cycle_day: Number(storageAccountForm.billing_cycle_day),
        payment_terms_days: Number(storageAccountForm.payment_terms_days),
        auto_invoice_enabled: storageAccountForm.auto_invoice_enabled,
        notes: storageAccountForm.notes.trim(),
      });
      setStorageAccountDialogOpen(false);
      setStorageAccountForm(initialStorageAccountForm);
      await loadRows();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSavingStorageAccount(false);
    }
  };

  useEffect(() => {
    if (page > 0 && page >= totalPages && totalPages > 0) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  const openReceiveDialog = async (sid) => {
    setError('');
    try {
      const [inbound, locationResult] = await Promise.all([
        getMarketplaceInboundShipment(sid),
        getMarketplaceWarehouseLocations(),
      ]);
      setSelectedInbound(inbound);
      setLocations(locationResult.rows.filter((location) => location.warehouse_sid === inbound.warehouse_sid && location.is_active));
      setReceiptLines(inbound.lines.filter((line) => line.received_quantity < line.expected_quantity).map((line) => ({
        inbound_line_sid: line.sid,
        label_code: line.label_code,
        product_label: `${line.product_title} - ${line.variant_title}`,
        remaining_quantity: line.expected_quantity - line.received_quantity,
        location_sid: '',
        accepted_quantity: line.expected_quantity - line.received_quantity,
        damaged_quantity: 0,
        rejected_quantity: 0,
        unit_cbm: line.unit_cbm || 0,
      })));
      setReceiveDialogOpen(true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to open receiving workflow.');
    }
  };

  const updateReceiptLine = (index, field, value) => {
    setReceiptLines((current) => current.map((line, lineIndex) => (
      lineIndex === index ? { ...line, [field]: value } : line
    )));
  };

  const submitReceipt = async () => {
    setReceiving(true);
    setError('');
    try {
      await receiveMarketplaceInboundShipment(selectedInbound.sid, {
        lines: receiptLines.map(({ inbound_line_sid, location_sid, accepted_quantity, damaged_quantity, rejected_quantity }) => ({
          unit_cbm: Number(receiptLines.find((line) => line.inbound_line_sid === inbound_line_sid)?.unit_cbm || 0),
          inbound_line_sid,
          location_sid,
          accepted_quantity: Number(accepted_quantity),
          damaged_quantity: Number(damaged_quantity),
          rejected_quantity: Number(rejected_quantity),
        })),
      });
      setReceiveDialogOpen(false);
      await loadRows();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setReceiving(false);
    }
  };

  const openScannedLabel = async () => {
    if (!scanLabel.trim()) return;
    setError('');
    try {
      const result = await scanMarketplaceWarehouseLabel(scanLabel.trim());
      await openReceiveDialog(result.inbound_shipment_sid);
      setScanLabel('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Warehouse label not found.');
    }
  };

  const generateCharges = async () => {
    setError('');
    try {
      await generateMarketplaceStorageCharges();
      await loadRows();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to generate storage charges.');
    }
  };

  const handleIssueInvoice = async (storageAccountSid) => {
    setIssuingInvoiceFor(storageAccountSid);
    setError('');
    try {
      await issueMarketplaceInvoice(storageAccountSid);
      await loadRows();
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to issue invoice.');
    } finally {
      setIssuingInvoiceFor('');
    }
  };

  const handleOpenCreateWarehouse = () => {
    setEditingWarehouseSid('');
    setWarehouseForm(initialWarehouseForm);
    setWarehouseDialogOpen(true);
  };

  const handleOpenEditWarehouse = (warehouseRow) => {
    setEditingWarehouseSid(warehouseRow.sid);
    setWarehouseForm({
      code: warehouseRow.code || '',
      name: warehouseRow.name || '',
      city: warehouseRow.city || '',
      state: warehouseRow.state || '',
      country: warehouseRow.country || 'Nigeria',
      address: warehouseRow.address || '',
      floor_area_sqm: warehouseRow.floor_area_sqm ?? '',
      ceiling_height_m: warehouseRow.ceiling_height_m ?? '',
      max_capacity_cbm: warehouseRow.max_capacity_cbm ?? '',
      is_active: warehouseRow.is_active ?? true,
    });
    setWarehouseDialogOpen(true);
  };

  const handleWarehouseFormChange = (field) => (event) => {
    setWarehouseForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleWarehouseStatusChange = (event) => {
    setWarehouseForm((current) => ({
      ...current,
      is_active: event.target.value === 'true',
    }));
  };

  const handleSaveWarehouse = async () => {
    setSavingWarehouse(true);
    setError('');
    try {
      const payload = {
        code: warehouseForm.code.trim(),
        name: warehouseForm.name.trim(),
        city: warehouseForm.city.trim() || null,
        state: warehouseForm.state.trim() || null,
        country: warehouseForm.country.trim() || 'Nigeria',
        address: warehouseForm.address.trim(),
        floor_area_sqm: warehouseForm.floor_area_sqm === '' ? null : Number(warehouseForm.floor_area_sqm),
        ceiling_height_m: warehouseForm.ceiling_height_m === '' ? null : Number(warehouseForm.ceiling_height_m),
        max_capacity_cbm: warehouseForm.max_capacity_cbm === '' ? null : Number(warehouseForm.max_capacity_cbm),
        is_active: warehouseForm.is_active,
      };

      if (editingWarehouseSid) {
        await updateMarketplaceWarehouse(editingWarehouseSid, payload);
      } else {
        await createMarketplaceWarehouse(payload);
      }

      setWarehouseDialogOpen(false);
      setWarehouseForm(initialWarehouseForm);
      setEditingWarehouseSid('');
      await loadRows();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSavingWarehouse(false);
    }
  };

  const handleOpenCreateRateCard = () => {
    setEditingRateCardSid('');
    setRateCardForm(initialRateCardForm);
    setRateCardDialogOpen(true);
  };

  const handleOpenEditRateCard = (rateCardRow) => {
    setEditingRateCardSid(rateCardRow.sid);
    setRateCardForm({
      warehouse_sid: rateCardRow.warehouse_sid || '',
      name: rateCardRow.name || '',
      currency: rateCardRow.currency || 'NGN',
      receiving_fee_per_cbm: rateCardRow.receiving_fee_per_cbm ?? '',
      receiving_pricing_mode: rateCardRow.receiving_pricing_mode || 'flat',
      receiving_percentage_rate: rateCardRow.receiving_percentage_rate ?? '',
      receiving_minimum_fee: rateCardRow.receiving_minimum_fee ?? '',
      storage_fee_per_cbm_per_month: rateCardRow.storage_fee_per_cbm_per_month ?? '',
      storage_pricing_mode: rateCardRow.storage_pricing_mode || 'flat',
      storage_percentage_rate: rateCardRow.storage_percentage_rate ?? '',
      overstay_fee_per_cbm_per_month: rateCardRow.overstay_fee_per_cbm_per_month ?? '',
      overstay_pricing_mode: rateCardRow.overstay_pricing_mode || 'flat',
      overstay_percentage_rate: rateCardRow.overstay_percentage_rate ?? '',
      overstay_minimum_fee: rateCardRow.overstay_minimum_fee ?? '',
      overstay_after_months: rateCardRow.overstay_after_months ?? 6,
      fulfillment_fee_per_order: rateCardRow.fulfillment_fee_per_order ?? '',
      fulfillment_pricing_mode: rateCardRow.fulfillment_pricing_mode || 'flat',
      fulfillment_percentage_rate: rateCardRow.fulfillment_percentage_rate ?? '',
      fulfillment_minimum_fee: rateCardRow.fulfillment_minimum_fee ?? '',
      pick_fee_per_item: rateCardRow.pick_fee_per_item ?? '',
      pick_pricing_mode: rateCardRow.pick_pricing_mode || 'flat',
      pick_percentage_rate: rateCardRow.pick_percentage_rate ?? '',
      pick_minimum_fee: rateCardRow.pick_minimum_fee ?? '',
      packaging_fee_per_package: rateCardRow.packaging_fee_per_package ?? '',
      packaging_pricing_mode: rateCardRow.packaging_pricing_mode || 'flat',
      packaging_percentage_rate: rateCardRow.packaging_percentage_rate ?? '',
      packaging_minimum_fee: rateCardRow.packaging_minimum_fee ?? '',
      shipping_handling_fee_per_package: rateCardRow.shipping_handling_fee_per_package ?? '',
      shipping_handling_pricing_mode: rateCardRow.shipping_handling_pricing_mode || 'flat',
      shipping_handling_percentage_rate: rateCardRow.shipping_handling_percentage_rate ?? '',
      shipping_handling_minimum_fee: rateCardRow.shipping_handling_minimum_fee ?? '',
      minimum_storage_charge: rateCardRow.minimum_storage_charge ?? '',
      declared_value_alert_percent_of_listing: rateCardRow.declared_value_alert_percent_of_listing ?? 50,
      grace_period_days: rateCardRow.grace_period_days ?? 0,
      is_default: rateCardRow.is_default ?? false,
      is_active: rateCardRow.is_active ?? true,
      notes: rateCardRow.notes || '',
    });
    setRateCardDialogOpen(true);
  };

  const handleRateCardFormChange = (field) => (event) => {
    setRateCardForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleRateCardBooleanChange = (field) => (event) => {
    setRateCardForm((current) => ({
      ...current,
      [field]: event.target.value === 'true',
    }));
  };

  const parseOptionalNumber = (value) => (value === '' ? null : Number(value));

  const handleSaveRateCard = async () => {
    setSavingRateCard(true);
    setError('');
    try {
      const payload = {
        warehouse_sid: rateCardForm.warehouse_sid || null,
        name: rateCardForm.name.trim(),
        currency: rateCardForm.currency,
        receiving_fee_per_cbm: parseOptionalNumber(rateCardForm.receiving_fee_per_cbm) ?? 0,
        receiving_pricing_mode: rateCardForm.receiving_pricing_mode,
        receiving_percentage_rate: parseOptionalNumber(rateCardForm.receiving_percentage_rate) ?? 0,
        receiving_minimum_fee: parseOptionalNumber(rateCardForm.receiving_minimum_fee) ?? 0,
        storage_fee_per_cbm_per_month: parseOptionalNumber(rateCardForm.storage_fee_per_cbm_per_month) ?? 0,
        storage_pricing_mode: rateCardForm.storage_pricing_mode,
        storage_percentage_rate: parseOptionalNumber(rateCardForm.storage_percentage_rate) ?? 0,
        overstay_fee_per_cbm_per_month: parseOptionalNumber(rateCardForm.overstay_fee_per_cbm_per_month) ?? 0,
        overstay_pricing_mode: rateCardForm.overstay_pricing_mode,
        overstay_percentage_rate: parseOptionalNumber(rateCardForm.overstay_percentage_rate) ?? 0,
        overstay_minimum_fee: parseOptionalNumber(rateCardForm.overstay_minimum_fee) ?? 0,
        overstay_after_months: Number(rateCardForm.overstay_after_months || 0),
        fulfillment_fee_per_order: parseOptionalNumber(rateCardForm.fulfillment_fee_per_order) ?? 0,
        fulfillment_pricing_mode: rateCardForm.fulfillment_pricing_mode,
        fulfillment_percentage_rate: parseOptionalNumber(rateCardForm.fulfillment_percentage_rate) ?? 0,
        fulfillment_minimum_fee: parseOptionalNumber(rateCardForm.fulfillment_minimum_fee) ?? 0,
        pick_fee_per_item: parseOptionalNumber(rateCardForm.pick_fee_per_item) ?? 0,
        pick_pricing_mode: rateCardForm.pick_pricing_mode,
        pick_percentage_rate: parseOptionalNumber(rateCardForm.pick_percentage_rate) ?? 0,
        pick_minimum_fee: parseOptionalNumber(rateCardForm.pick_minimum_fee) ?? 0,
        packaging_fee_per_package: parseOptionalNumber(rateCardForm.packaging_fee_per_package) ?? 0,
        packaging_pricing_mode: rateCardForm.packaging_pricing_mode,
        packaging_percentage_rate: parseOptionalNumber(rateCardForm.packaging_percentage_rate) ?? 0,
        packaging_minimum_fee: parseOptionalNumber(rateCardForm.packaging_minimum_fee) ?? 0,
        shipping_handling_fee_per_package: parseOptionalNumber(rateCardForm.shipping_handling_fee_per_package) ?? 0,
        shipping_handling_pricing_mode: rateCardForm.shipping_handling_pricing_mode,
        shipping_handling_percentage_rate: parseOptionalNumber(rateCardForm.shipping_handling_percentage_rate) ?? 0,
        shipping_handling_minimum_fee: parseOptionalNumber(rateCardForm.shipping_handling_minimum_fee) ?? 0,
        minimum_storage_charge: parseOptionalNumber(rateCardForm.minimum_storage_charge) ?? 0,
        declared_value_alert_percent_of_listing: parseOptionalNumber(rateCardForm.declared_value_alert_percent_of_listing) ?? 50,
        grace_period_days: Number(rateCardForm.grace_period_days || 0),
        is_default: rateCardForm.is_default,
        is_active: rateCardForm.is_active,
        notes: rateCardForm.notes.trim() || '',
      };

      if (editingRateCardSid) {
        await updateMarketplaceRateCard(editingRateCardSid, payload);
      } else {
        await createMarketplaceRateCard(payload);
      }

      setRateCardDialogOpen(false);
      setRateCardForm(initialRateCardForm);
      setEditingRateCardSid('');
      await loadRows();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSavingRateCard(false);
    }
  };

  const openInboundReviewDialog = async (sid) => {
    setError('');
    try {
      const inbound = await getMarketplaceInboundShipment(sid);
      setInboundReviewForm({
        sid: inbound.sid,
        inbound_number: inbound.inbound_number,
        declared_total_value: inbound.declared_total_value ?? 0,
        reviewed_total_value: inbound.reviewed_total_value ?? '',
        is_value_flagged: inbound.is_value_flagged ?? false,
        value_review_notes: inbound.value_review_notes || '',
        lines: inbound.lines || [],
      });
      setReviewDialogOpen(true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || 'Unable to open inbound value review.');
    }
  };

  const handleInboundReviewFieldChange = (field) => (event) => {
    setInboundReviewForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleSaveInboundReview = async () => {
    if (!inboundReviewForm.sid) return;
    setSavingInboundReview(true);
    setError('');
    try {
      await updateMarketplaceInboundShipment(inboundReviewForm.sid, {
        reviewed_total_value: inboundReviewForm.reviewed_total_value === '' ? null : Number(inboundReviewForm.reviewed_total_value),
        value_review_notes: inboundReviewForm.value_review_notes.trim(),
      });
      setReviewDialogOpen(false);
      setInboundReviewForm(initialInboundReviewForm);
      await loadRows();
    } catch (requestError) {
      setError(JSON.stringify(requestError.response?.data || requestError.message));
    } finally {
      setSavingInboundReview(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 0 || newPage >= totalPages) return;
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput('');
      return;
    }
    setRowsPerPage(Number(value));
    setCustomRowsActive(false);
    setCustomRowsInput('');
    setPage(0);
  };

  const handleCustomRowsInputChange = (event) => {
    setCustomRowsInput(event.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const numericValue = Number(customRowsInput);
    if (numericValue > 0) {
      setRowsPerPage(numericValue);
      setPage(0);
    }
    setCustomRowsActive(false);
    setCustomRowsInput('');
  };

  const columns = useMemo(() => {
    if (location.pathname === '/admin/merchant-admin/warehouses') {
      return [
        ...config.columns,
        {
          field: 'actions',
          headerName: 'Actions',
          width: 130,
          sortable: false,
          renderCell: ({ row }) => (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => handleOpenEditWarehouse(row)}
            >
              Edit
            </Button>
          ),
        },
      ];
    }
    if (location.pathname === '/admin/merchant-admin/rate-cards') {
      return [
        ...config.columns,
        {
          field: 'actions',
          headerName: 'Actions',
          width: 130,
          sortable: false,
          renderCell: ({ row }) => (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => handleOpenEditRateCard(row)}
            >
              Edit
            </Button>
          ),
        },
      ];
    }
    if (location.pathname === '/admin/merchant-admin/inbound-shipments') {
      return [
        ...config.columns,
        {
          field: 'actions',
          headerName: 'Actions',
          width: 240,
          sortable: false,
          renderCell: ({ row }) => (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" color={row.is_value_flagged ? 'warning' : 'primary'} onClick={() => openInboundReviewDialog(row.sid)}>
                Review Value
              </Button>
              <Button size="small" variant="outlined" disabled={row.status === 'received' || row.status === 'cancelled'} onClick={() => openReceiveDialog(row.sid)}>
                Receive
              </Button>
            </Box>
          ),
        },
      ];
    }
    if (location.pathname === '/admin/merchant-admin/storage-accounts') {
      return [
        ...config.columns,
        {
          field: 'actions',
          headerName: 'Actions',
          width: 170,
          sortable: false,
          renderCell: ({ row }) => (
            <Button
              size="small"
              variant="outlined"
              disabled={issuingInvoiceFor === row.sid || row.status !== 'active'}
              onClick={() => handleIssueInvoice(row.sid)}
            >
              {issuingInvoiceFor === row.sid ? 'Issuing...' : 'Issue Invoice'}
            </Button>
          ),
        },
      ];
    }
    return config.columns;
  }, [config.columns, issuingInvoiceFor, location.pathname]);

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden',
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: 3,
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>{config.title}</Typography>
          <Typography color="text.secondary">{config.description}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadRows}>
            Refresh
          </Button>
          {location.pathname === '/admin/merchant-admin/warehouses' && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateWarehouse}>
              Add Warehouse
            </Button>
          )}
          {location.pathname === '/admin/merchant-admin/rate-cards' && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateRateCard}>
              Add Rate Card
            </Button>
          )}
          {location.pathname === '/admin/merchant-admin/storage-accounts' && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreateStorageAccount}>
              Create Storage Account
            </Button>
          )}
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {location.pathname === '/admin/merchant-admin/inbound-shipments' && (
        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Scan or enter warehouse label"
            value={scanLabel}
            onChange={(event) => setScanLabel(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && openScannedLabel()}
            sx={{ minWidth: 320 }}
          />
          <Button variant="contained" onClick={openScannedLabel}>Open Label</Button>
          <Button variant="outlined" onClick={generateCharges}>Generate Storage Charges</Button>
        </Box>
      )}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              autoHeight
              rows={paginatedRows}
              columns={columns}
              disableRowSelectionOnClick
              hideFooter
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: theme.palette.primary.main,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: theme.palette.primary.contrastText,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton': {
                  color: theme.palette.primary.contrastText,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            />
          )}
        </CardContent>
      </Card>
      {!loading && (
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      )}

      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Receive {selectedInbound?.inbound_number}</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Assign a warehouse bin and confirm the condition of each received SKU.
          </Typography>
          <Alert severity={selectedInbound?.is_value_flagged ? 'warning' : 'info'} sx={{ mb: 2 }}>
            Declared value: {selectedInbound?.declared_total_value || 0}
            {' | '}
            Reviewed value: {selectedInbound?.reviewed_total_value ?? 'Not set'}
          </Alert>
          {receiptLines.map((line, index) => (
            <Card variant="outlined" key={line.inbound_line_sid} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1">{line.product_label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Label: {line.label_code} | Remaining expected: {line.remaining_quantity}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required>
                      <InputLabel>Warehouse Bin</InputLabel>
                      <Select label="Warehouse Bin" value={line.location_sid} onChange={(event) => updateReceiptLine(index, 'location_sid', event.target.value)}>
                        {locations.map((warehouseLocation) => <MenuItem key={warehouseLocation.sid} value={warehouseLocation.sid}>{warehouseLocation.code} - {warehouseLocation.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 4, md: 2.5 }}><TextField fullWidth type="number" label="Accepted" value={line.accepted_quantity} onChange={(event) => updateReceiptLine(index, 'accepted_quantity', event.target.value)} inputProps={{ min: 0 }} /></Grid>
                  <Grid size={{ xs: 4, md: 2.5 }}><TextField fullWidth type="number" label="Damaged" value={line.damaged_quantity} onChange={(event) => updateReceiptLine(index, 'damaged_quantity', event.target.value)} inputProps={{ min: 0 }} /></Grid>
                  <Grid size={{ xs: 4, md: 2.5 }}><TextField fullWidth type="number" label="Rejected" value={line.rejected_quantity} onChange={(event) => updateReceiptLine(index, 'rejected_quantity', event.target.value)} inputProps={{ min: 0 }} /></Grid>
                  <Grid size={{ xs: 12, md: 2.5 }}><TextField fullWidth type="number" label="Unit CBM" value={line.unit_cbm} onChange={(event) => updateReceiptLine(index, 'unit_cbm', event.target.value)} inputProps={{ min: 0, step: '0.001' }} /></Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={receiving || !receiptLines.length || receiptLines.some((line) => !line.location_sid)} onClick={submitReceipt}>
            {receiving ? 'Receiving...' : 'Confirm Receipt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewDialogOpen} onClose={() => !savingInboundReview && setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
          Review Inbound Value {inboundReviewForm.inbound_number ? `- ${inboundReviewForm.inbound_number}` : ''}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Declared Total Value"
                fullWidth
                value={inboundReviewForm.declared_total_value}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Reviewed Total Value"
                fullWidth
                type="number"
                value={inboundReviewForm.reviewed_total_value}
                onChange={handleInboundReviewFieldChange('reviewed_total_value')}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Leave empty to keep using the merchant declared value."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Value Alert"
                fullWidth
                value={inboundReviewForm.is_value_flagged ? 'Flagged' : 'Clear'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Review Notes"
                fullWidth
                multiline
                minRows={3}
                value={inboundReviewForm.value_review_notes}
                onChange={handleInboundReviewFieldChange('value_review_notes')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                SKU Value Check
              </Typography>
            </Grid>
            {inboundReviewForm.lines.map((line) => (
              <Grid size={{ xs: 12 }} key={line.sid}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2">
                      {line.product_title} - {line.variant_title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Qty: {line.expected_quantity} | Declared unit value: {line.declared_unit_value || 0}
                    </Typography>
                    <Typography variant="body2" color={line.is_value_flagged ? 'warning.main' : 'text.secondary'}>
                      {line.is_value_flagged ? (line.value_flag_reason || 'Low declared value flagged for review.') : 'No value alert on this SKU.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)} disabled={savingInboundReview}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveInboundReview} disabled={savingInboundReview}>
            {savingInboundReview ? 'Saving...' : 'Save Review'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={warehouseDialogOpen} onClose={() => !savingWarehouse && setWarehouseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
          {editingWarehouseSid ? 'Edit Warehouse' : 'Add Warehouse'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Warehouse Code"
                fullWidth
                required
                value={warehouseForm.code}
                onChange={handleWarehouseFormChange('code')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Warehouse Name"
                fullWidth
                required
                value={warehouseForm.name}
                onChange={handleWarehouseFormChange('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="City"
                fullWidth
                value={warehouseForm.city}
                onChange={handleWarehouseFormChange('city')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="State"
                fullWidth
                value={warehouseForm.state}
                onChange={handleWarehouseFormChange('state')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Country"
                fullWidth
                value={warehouseForm.country}
                onChange={handleWarehouseFormChange('country')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Floor Area (sqm)"
                fullWidth
                type="number"
                value={warehouseForm.floor_area_sqm}
                onChange={handleWarehouseFormChange('floor_area_sqm')}
                inputProps={{ min: 0, step: '0.001' }}
                helperText="Physical floor area used as part of actual CBM estimation."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Ceiling Height (m)"
                fullWidth
                type="number"
                value={warehouseForm.ceiling_height_m}
                onChange={handleWarehouseFormChange('ceiling_height_m')}
                inputProps={{ min: 0, step: '0.001' }}
                helperText="Physical ceiling height used as part of actual CBM estimation."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Capacity (CBM)"
                fullWidth
                type="number"
                value={warehouseForm.max_capacity_cbm}
                onChange={handleWarehouseFormChange('max_capacity_cbm')}
                inputProps={{ min: 0, step: '0.001' }}
                helperText="Usable CBM entered manually because actual CBM and usable CBM are not the same."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address"
                fullWidth
                required
                multiline
                minRows={3}
                value={warehouseForm.address}
                onChange={handleWarehouseFormChange('address')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={String(warehouseForm.is_active)}
                  onChange={handleWarehouseStatusChange}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarehouseDialogOpen(false)} disabled={savingWarehouse}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveWarehouse}
            disabled={
              savingWarehouse
              || !warehouseForm.code.trim()
              || !warehouseForm.name.trim()
              || !warehouseForm.address.trim()
            }
          >
            {savingWarehouse ? 'Saving...' : editingWarehouseSid ? 'Update Warehouse' : 'Create Warehouse'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rateCardDialogOpen} onClose={() => !savingRateCard && setRateCardDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
          {editingRateCardSid ? 'Edit Rate Card' : 'Add Rate Card'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Rate Card Name"
                fullWidth
                required
                value={rateCardForm.name}
                onChange={handleRateCardFormChange('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Warehouse Scope</InputLabel>
                <Select
                  label="Warehouse Scope"
                  value={rateCardForm.warehouse_sid}
                  onChange={handleRateCardFormChange('warehouse_sid')}
                >
                  <MenuItem value="">Global Default</MenuItem>
                  {warehouseOptions.map((warehouse) => (
                    <MenuItem key={warehouse.sid} value={warehouse.sid}>
                      {warehouse.code} - {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  label="Currency"
                  value={rateCardForm.currency}
                  onChange={handleRateCardFormChange('currency')}
                >
                  <MenuItem value="NGN">NGN</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Receiving Pricing
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Receiving Mode</InputLabel>
                <Select
                  label="Receiving Mode"
                  value={rateCardForm.receiving_pricing_mode}
                  onChange={handleRateCardFormChange('receiving_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Receiving Flat Fee / CBM"
                fullWidth
                type="number"
                value={rateCardForm.receiving_fee_per_cbm}
                onChange={handleRateCardFormChange('receiving_fee_per_cbm')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Receiving Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.receiving_percentage_rate}
                onChange={handleRateCardFormChange('receiving_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Receiving Minimum Fee"
                fullWidth
                type="number"
                value={rateCardForm.receiving_minimum_fee}
                onChange={handleRateCardFormChange('receiving_minimum_fee')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Storage Pricing
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Storage Mode</InputLabel>
                <Select
                  label="Storage Mode"
                  value={rateCardForm.storage_pricing_mode}
                  onChange={handleRateCardFormChange('storage_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Storage Flat Fee / CBM / Month"
                fullWidth
                type="number"
                value={rateCardForm.storage_fee_per_cbm_per_month}
                onChange={handleRateCardFormChange('storage_fee_per_cbm_per_month')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Storage Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.storage_percentage_rate}
                onChange={handleRateCardFormChange('storage_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Overstay Pricing
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Overstay Mode</InputLabel>
                <Select
                  label="Overstay Mode"
                  value={rateCardForm.overstay_pricing_mode}
                  onChange={handleRateCardFormChange('overstay_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Overstay Flat Fee / CBM / Month"
                fullWidth
                type="number"
                value={rateCardForm.overstay_fee_per_cbm_per_month}
                onChange={handleRateCardFormChange('overstay_fee_per_cbm_per_month')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Overstay Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.overstay_percentage_rate}
                onChange={handleRateCardFormChange('overstay_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Overstay Minimum Fee"
                fullWidth
                type="number"
                value={rateCardForm.overstay_minimum_fee}
                onChange={handleRateCardFormChange('overstay_minimum_fee')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Overstay After (Months)"
                fullWidth
                type="number"
                value={rateCardForm.overstay_after_months}
                onChange={handleRateCardFormChange('overstay_after_months')}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Low Value Alert Threshold (%)"
                fullWidth
                type="number"
                value={rateCardForm.declared_value_alert_percent_of_listing}
                onChange={handleRateCardFormChange('declared_value_alert_percent_of_listing')}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Flag inbound declarations below this percent of current listing value."
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Fulfillment Pricing
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Fulfillment Mode</InputLabel>
                <Select
                  label="Fulfillment Mode"
                  value={rateCardForm.fulfillment_pricing_mode}
                  onChange={handleRateCardFormChange('fulfillment_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Fulfillment Flat Fee / Order"
                fullWidth
                type="number"
                value={rateCardForm.fulfillment_fee_per_order}
                onChange={handleRateCardFormChange('fulfillment_fee_per_order')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Fulfillment Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.fulfillment_percentage_rate}
                onChange={handleRateCardFormChange('fulfillment_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Fulfillment Minimum Fee"
                fullWidth
                type="number"
                value={rateCardForm.fulfillment_minimum_fee}
                onChange={handleRateCardFormChange('fulfillment_minimum_fee')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Additional Item Picking Pricing (Optional Internal Audit)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Additional Item Picking Mode</InputLabel>
                <Select
                  label="Additional Item Picking Mode"
                  value={rateCardForm.pick_pricing_mode}
                  onChange={handleRateCardFormChange('pick_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Additional Item Picking Fee / Item"
                fullWidth
                type="number"
                value={rateCardForm.pick_fee_per_item}
                onChange={handleRateCardFormChange('pick_fee_per_item')}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Optional internal cost. Merchants see it inside the combined fulfillment fee."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Additional Item Picking Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.pick_percentage_rate}
                onChange={handleRateCardFormChange('pick_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Additional Item Picking Minimum Fee"
                fullWidth
                type="number"
                value={rateCardForm.pick_minimum_fee}
                onChange={handleRateCardFormChange('pick_minimum_fee')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                Shipping Handling Pricing
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Shipping Mode</InputLabel>
                <Select
                  label="Shipping Mode"
                  value={rateCardForm.shipping_handling_pricing_mode}
                  onChange={handleRateCardFormChange('shipping_handling_pricing_mode')}
                >
                  {PRICING_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Shipping Handling Flat Fee / Package"
                fullWidth
                type="number"
                value={rateCardForm.shipping_handling_fee_per_package}
                onChange={handleRateCardFormChange('shipping_handling_fee_per_package')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Shipping Handling Percentage (%)"
                fullWidth
                type="number"
                value={rateCardForm.shipping_handling_percentage_rate}
                onChange={handleRateCardFormChange('shipping_handling_percentage_rate')}
                inputProps={{ min: 0, step: '0.0001' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                label="Shipping Handling Minimum Fee"
                fullWidth
                type="number"
                value={rateCardForm.shipping_handling_minimum_fee}
                onChange={handleRateCardFormChange('shipping_handling_minimum_fee')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Minimum Storage Charge"
                fullWidth
                type="number"
                value={rateCardForm.minimum_storage_charge}
                onChange={handleRateCardFormChange('minimum_storage_charge')}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Grace Period (Days)"
                fullWidth
                type="number"
                value={rateCardForm.grace_period_days}
                onChange={handleRateCardFormChange('grace_period_days')}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Default Rate</InputLabel>
                <Select
                  label="Default Rate"
                  value={String(rateCardForm.is_default)}
                  onChange={handleRateCardBooleanChange('is_default')}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={String(rateCardForm.is_active)}
                  onChange={handleRateCardBooleanChange('is_active')}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                minRows={3}
                value={rateCardForm.notes}
                onChange={handleRateCardFormChange('notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateCardDialogOpen(false)} disabled={savingRateCard}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRateCard}
            disabled={savingRateCard || !rateCardForm.name.trim()}
          >
            {savingRateCard ? 'Saving...' : editingRateCardSid ? 'Update Rate Card' : 'Create Rate Card'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={storageAccountDialogOpen} onClose={() => !savingStorageAccount && setStorageAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
          Create Merchant Storage Account
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Provision a merchant for a warehouse before stock arrives. If this is skipped, the first merchant inbound shipment creates the account automatically using the default rate card.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Merchant</InputLabel>
                <Select label="Merchant" value={storageAccountForm.merchant_sid} onChange={handleStorageAccountFormChange('merchant_sid')}>
                  {merchantOptions.map((merchant) => (
                    <MenuItem key={merchant.sid} value={merchant.sid}>
                      {merchant.business_name || merchant.full_name || merchant.sid}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Warehouse</InputLabel>
                <Select label="Warehouse" value={storageAccountForm.warehouse_sid} onChange={handleStorageAccountFormChange('warehouse_sid')}>
                  {warehouseOptions.filter((warehouse) => warehouse.is_active).map((warehouse) => (
                    <MenuItem key={warehouse.sid} value={warehouse.sid}>
                      {warehouse.code} - {warehouse.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Rate Card</InputLabel>
                <Select label="Rate Card" value={storageAccountForm.rate_card_sid} onChange={handleStorageAccountFormChange('rate_card_sid')}>
                  <MenuItem value="">Use Default Rate Card</MenuItem>
                  {storageRateCardOptions
                    .filter((rateCard) => rateCard.is_active && (!rateCard.warehouse_sid || rateCard.warehouse_sid === storageAccountForm.warehouse_sid))
                    .map((rateCard) => (
                      <MenuItem key={rateCard.sid} value={rateCard.sid}>
                        {rateCard.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Billing Day" type="number" fullWidth value={storageAccountForm.billing_cycle_day} onChange={handleStorageAccountFormChange('billing_cycle_day')} inputProps={{ min: 1, max: 28 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Payment Terms (Days)" type="number" fullWidth value={storageAccountForm.payment_terms_days} onChange={handleStorageAccountFormChange('payment_terms_days')} inputProps={{ min: 0 }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Automatic Invoices</InputLabel>
                <Select label="Automatic Invoices" value={String(storageAccountForm.auto_invoice_enabled)} onChange={handleStorageAccountBooleanChange}>
                  <MenuItem value="true">Enabled</MenuItem>
                  <MenuItem value="false">Disabled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes" fullWidth multiline minRows={3} value={storageAccountForm.notes} onChange={handleStorageAccountFormChange('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStorageAccountDialogOpen(false)} disabled={savingStorageAccount}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveStorageAccount}
            disabled={savingStorageAccount || !storageAccountForm.merchant_sid || !storageAccountForm.warehouse_sid}
          >
            {savingStorageAccount ? 'Creating...' : 'Create Storage Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceWarehousePage;
